import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { GOVERNANCE } from "@/constants/testIds";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart,
} from "recharts";
import {
    TrendingUp, Users, AlertTriangle, ShieldCheck, DollarSign, Zap, Save,
} from "lucide-react";

function fmt$(v) {
    if (v == null) return "$0.00";
    if (v < 0.01 && v > 0) return `$${v.toFixed(4)}`;
    return `$${v.toFixed(2)}`;
}
function fmtInt(v) {
    return (v || 0).toLocaleString();
}

function KPI({ testId, label, value, sub, accent = false }) {
    return (
        <div
            data-testid={testId}
            className={`bg-white rounded-lg border card-ambient p-5 ${
                accent
                    ? "border-[color:var(--profecia-gold-subtle)]"
                    : "border-[color:var(--border-subtle)]"
            }`}
        >
            <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--text-muted)]">
                {label}
            </div>
            <div className="font-serif text-3xl text-[color:var(--text-main)] mt-2 leading-none">
                {value}
            </div>
            {sub && (
                <div className="text-[11px] text-[color:var(--text-muted)] font-mono mt-2">
                    {sub}
                </div>
            )}
        </div>
    );
}

function BudgetBar({ testId, label, spend, cap, softPct }) {
    const pct = cap > 0 ? Math.min(100, (spend / cap) * 100) : 0;
    const level =
        pct >= 100 ? "critical" : pct >= (softPct || 80) ? "warning" : "ok";
    const barColor =
        level === "critical" ? "#b91c1c" : level === "warning" ? "#c5a059" : "#6b7f5b";
    return (
        <div
            data-testid={testId}
            className="bg-white rounded-lg border border-[color:var(--border-subtle)] card-ambient p-5"
        >
            <div className="flex items-baseline justify-between">
                <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--text-muted)]">
                    {label}
                </div>
                <div className="text-[11px] font-mono text-[color:var(--text-muted)]">
                    {fmt$(spend)} / {fmt$(cap)}
                </div>
            </div>
            <div className="mt-3 h-2 bg-[color:var(--surface-hover)] rounded-full overflow-hidden">
                <div
                    className="h-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: barColor }}
                />
            </div>
            <div className="text-[11px] mt-2 font-mono text-[color:var(--text-muted)]">
                {pct.toFixed(1)}% used ·{" "}
                {level === "critical"
                    ? "budget exhausted"
                    : level === "warning"
                      ? `over soft threshold (${softPct || 80}%)`
                      : "within budget"}
            </div>
        </div>
    );
}

export default function GovernancePage() {
    const { user, isAdmin } = useAuth();
    const [summary, setSummary] = useState(null);
    const [timeseries, setTimeseries] = useState([]);
    const [users, setUsers] = useState([]);
    const [orgBudgetInput, setOrgBudgetInput] = useState("");
    const [saving, setSaving] = useState(null); // user_id | 'org' | null
    const [budgetEdits, setBudgetEdits] = useState({});
    const [err, setErr] = useState(null);
    const [saved, setSaved] = useState(null);

    const load = async () => {
        try {
            const [s, ts, us] = await Promise.all([
                api.get("/governance/summary"),
                api.get("/governance/timeseries?days=14"),
                isAdmin ? api.get("/governance/users") : Promise.resolve({ data: [] }),
            ]);
            setSummary(s.data);
            setTimeseries(ts.data);
            setUsers(us.data);
            setOrgBudgetInput(String(s.data.budgets.org_monthly_usd || ""));
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const saveOrgBudget = async () => {
        setSaving("org");
        setErr(null);
        try {
            await api.put("/governance/budgets", {
                user_id: null,
                monthly_budget_usd: Number(orgBudgetInput),
            });
            setSaved("org");
            setTimeout(() => setSaved(null), 1500);
            await load();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setSaving(null);
        }
    };

    const saveUserBudget = async (u) => {
        const val = Number(budgetEdits[u.id] ?? u.monthly_budget_usd);
        setSaving(u.id);
        setErr(null);
        try {
            await api.put("/governance/budgets", {
                user_id: u.id,
                monthly_budget_usd: val,
            });
            setSaved(u.id);
            setTimeout(() => setSaved(null), 1500);
            await load();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setSaving(null);
        }
    };

    if (!summary) {
        return (
            <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16 text-center">
                <div className="text-[color:var(--text-muted)] text-sm font-light">Loading governance dashboard…</div>
            </div>
        );
    }

    const alerts = summary.alerts || [];

    return (
        <main className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8 lg:py-10 space-y-8">
            <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">
                    Governance & Economics
                </div>
                <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--text-main)] leading-tight mt-1">
                    {isAdmin ? "Organization AI Spend Control" : "Your AI Spend"}
                </h2>
                <p className="text-sm text-[color:var(--text-muted)] font-light mt-2 max-w-2xl">
                    Real-time token & cost telemetry for the six-agent Higgsfield pipeline.
                    Budgets are enforced at request time — no runaway spend.
                </p>
            </div>

            {alerts.length > 0 && (
                <div data-testid={GOVERNANCE.alertBanner} className="space-y-2">
                    {alerts.map((a, i) => (
                        <div
                            key={i}
                            className={`flex items-start gap-3 px-4 py-3 rounded-md border ${
                                a.level === "critical"
                                    ? "bg-red-50 border-red-200 text-red-800"
                                    : "bg-[color:var(--profecia-gold-light)] border-[color:var(--profecia-gold-subtle)] text-[color:var(--profecia-gold)]"
                            }`}
                        >
                            {a.level === "critical" ? (
                                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                            ) : (
                                <ShieldCheck size={16} className="mt-0.5 shrink-0" />
                            )}
                            <div className="text-sm">
                                <div className="font-medium">{a.message}</div>
                                <div className="font-mono text-xs opacity-80 mt-0.5">
                                    {fmt$(a.spend)} of {fmt$(a.cap)} · {a.pct.toFixed(1)}%
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {err && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-2">
                    {String(err)}
                </div>
            )}

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPI
                    testId={GOVERNANCE.kpi24h}
                    label="Last 24 h"
                    value={fmt$(summary.totals["24h"].cost_usd)}
                    sub={`${summary.totals["24h"].run_count} runs · ${fmtInt(summary.totals["24h"].input_tokens + summary.totals["24h"].output_tokens)} tokens`}
                />
                <KPI
                    testId={GOVERNANCE.kpi7d}
                    label="Last 7 days"
                    value={fmt$(summary.totals["7d"].cost_usd)}
                    sub={`${summary.totals["7d"].run_count} runs`}
                />
                <KPI
                    testId={GOVERNANCE.kpi30d}
                    label="Last 30 days"
                    value={fmt$(summary.totals["30d"].cost_usd)}
                    sub={`${summary.totals["30d"].run_count} runs`}
                />
                <KPI
                    testId={GOVERNANCE.kpiMonth}
                    label="This month"
                    value={fmt$(summary.totals.month.cost_usd)}
                    sub={`${summary.totals.month.run_count} runs`}
                    accent
                />
            </div>

            {/* Budgets */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <BudgetBar
                    testId={GOVERNANCE.budgetBarUser}
                    label={`Your Budget (${user?.email})`}
                    spend={summary.budgets.user_month_spend_usd}
                    cap={summary.budgets.user_monthly_usd}
                    softPct={summary.budgets.soft_threshold_pct}
                />
                <BudgetBar
                    testId={GOVERNANCE.budgetBarOrg}
                    label="Organization Budget"
                    spend={summary.budgets.org_month_spend_usd}
                    cap={summary.budgets.org_monthly_usd}
                    softPct={summary.budgets.soft_threshold_pct}
                />
            </div>

            {/* Chart */}
            {timeseries.length > 0 && (
                <section
                    data-testid={GOVERNANCE.timeseriesChart}
                    className="bg-white rounded-lg border border-[color:var(--border-subtle)] card-ambient p-6"
                >
                    <div className="flex items-baseline justify-between mb-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">
                                Trend
                            </div>
                            <h3 className="font-serif text-2xl text-[color:var(--text-main)] leading-none mt-1">
                                Daily Cost — Last 14 days
                            </h3>
                        </div>
                        <TrendingUp size={18} className="text-[color:var(--profecia-gold)]" />
                    </div>
                    <div className="h-64">
                        <ResponsiveContainer>
                            <AreaChart data={timeseries}>
                                <defs>
                                    <linearGradient id="gold" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#C5A059" stopOpacity={0.4} />
                                        <stop offset="100%" stopColor="#C5A059" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="rgba(26,24,20,0.05)" vertical={false} />
                                <XAxis dataKey="date" stroke="#767269" fontSize={11} tickMargin={8} />
                                <YAxis
                                    stroke="#767269"
                                    fontSize={11}
                                    tickFormatter={(v) => `$${v.toFixed(2)}`}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: "#fff",
                                        border: "1px solid rgba(26,24,20,0.1)",
                                        borderRadius: 6,
                                        fontSize: 12,
                                    }}
                                    formatter={(value) => [`$${Number(value).toFixed(4)}`, "Cost"]}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="cost_usd"
                                    stroke="#C5A059"
                                    strokeWidth={2}
                                    fill="url(#gold)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </section>
            )}

            {/* By Agent / By Model */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <BreakdownCard title="Spend by Agent (30d)" rows={summary.by_agent} icon={Zap} />
                <BreakdownCard title="Spend by Model (30d)" rows={summary.by_model} icon={DollarSign} />
            </div>

            {/* Admin — per-user table + org budget */}
            {isAdmin && (
                <>
                    <section className="bg-white rounded-lg border border-[color:var(--border-subtle)] card-ambient p-6">
                        <div className="flex items-baseline justify-between mb-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">
                                    Admin · Org Budget
                                </div>
                                <h3 className="font-serif text-xl text-[color:var(--text-main)] leading-none mt-1">
                                    Set organization-wide monthly cap
                                </h3>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-end gap-3">
                            <label className="block">
                                <span className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)] mb-1.5 block">
                                    Monthly cap (USD)
                                </span>
                                <input
                                    data-testid={GOVERNANCE.orgBudgetInput}
                                    type="number"
                                    value={orgBudgetInput}
                                    step="10"
                                    min="0"
                                    onChange={(e) => setOrgBudgetInput(e.target.value)}
                                    className="w-48 px-3 py-2.5 rounded-md border border-[color:var(--border-subtle)] bg-white focus:outline-none focus:border-[color:var(--profecia-gold)]"
                                />
                            </label>
                            <button
                                data-testid={GOVERNANCE.orgBudgetSave}
                                onClick={saveOrgBudget}
                                disabled={saving === "org"}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[color:var(--profecia-gold)] text-white text-sm font-medium hover:bg-[color:var(--profecia-gold-hover)] disabled:opacity-50 transition-colors"
                            >
                                <Save size={14} /> {saved === "org" ? "Saved ✓" : "Save"}
                            </button>
                        </div>
                    </section>

                    <section
                        data-testid={GOVERNANCE.userTable}
                        className="bg-white rounded-lg border border-[color:var(--border-subtle)] card-ambient overflow-hidden"
                    >
                        <div className="px-6 py-4 border-b border-[color:var(--border-subtle)] flex items-center gap-2">
                            <Users size={16} className="text-[color:var(--profecia-gold)]" />
                            <h3 className="font-serif text-xl text-[color:var(--text-main)] leading-none">
                                Users — Per-user budgets & utilization
                            </h3>
                        </div>
                        <table className="w-full text-sm">
                            <thead className="text-left text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)] bg-[color:var(--surface-hover)]">
                                <tr>
                                    <th className="px-6 py-3">User</th>
                                    <th className="px-6 py-3">Role</th>
                                    <th className="px-6 py-3">Month spend</th>
                                    <th className="px-6 py-3">Monthly cap</th>
                                    <th className="px-6 py-3">Utilization</th>
                                    <th className="px-6 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => {
                                    const edit = budgetEdits[u.id];
                                    const val = edit !== undefined ? edit : u.monthly_budget_usd;
                                    return (
                                        <tr
                                            key={u.id}
                                            data-testid={GOVERNANCE.userRow(u.id)}
                                            className="border-t border-[color:var(--border-subtle)]"
                                        >
                                            <td className="px-6 py-3">
                                                <div className="text-[color:var(--text-main)]">{u.name || u.email}</div>
                                                <div className="text-[11px] text-[color:var(--text-muted)] font-mono">{u.email}</div>
                                            </td>
                                            <td className="px-6 py-3">
                                                <span className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--profecia-gold)]">
                                                    {u.role}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3 font-mono text-[color:var(--text-main)]">{fmt$(u.month_spend_usd)}</td>
                                            <td className="px-6 py-3">
                                                <input
                                                    type="number"
                                                    value={val}
                                                    min="0"
                                                    step="10"
                                                    onChange={(e) =>
                                                        setBudgetEdits((prev) => ({
                                                            ...prev,
                                                            [u.id]: e.target.value,
                                                        }))
                                                    }
                                                    className="w-24 px-2 py-1 rounded border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)] font-mono text-sm"
                                                />
                                            </td>
                                            <td className="px-6 py-3 font-mono text-[color:var(--text-main)]">
                                                {u.utilization_pct.toFixed(1)}%
                                            </td>
                                            <td className="px-6 py-3">
                                                <button
                                                    data-testid={GOVERNANCE.saveBudgetBtn(u.id)}
                                                    onClick={() => saveUserBudget(u)}
                                                    disabled={saving === u.id}
                                                    className="text-xs px-3 py-1.5 rounded-md bg-[color:var(--profecia-gold)] text-white hover:bg-[color:var(--profecia-gold-hover)] disabled:opacity-50 transition-colors"
                                                >
                                                    {saved === u.id ? "Saved ✓" : "Save"}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </section>
                </>
            )}
        </main>
    );
}

function BreakdownCard({ title, rows, icon: Icon }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="bg-white rounded-lg border border-[color:var(--border-subtle)] card-ambient p-6">
                <div className="flex items-baseline justify-between mb-4">
                    <h3 className="font-serif text-xl text-[color:var(--text-main)] leading-none">{title}</h3>
                    <Icon size={16} className="text-[color:var(--profecia-gold)]" />
                </div>
                <div className="text-sm text-[color:var(--text-muted)] font-light py-6 text-center">
                    No usage yet — run a pipeline to see numbers here.
                </div>
            </div>
        );
    }
    const maxCost = Math.max(...rows.map((r) => r.cost_usd)) || 1;
    return (
        <div className="bg-white rounded-lg border border-[color:var(--border-subtle)] card-ambient p-6">
            <div className="flex items-baseline justify-between mb-4">
                <h3 className="font-serif text-xl text-[color:var(--text-main)] leading-none">{title}</h3>
                <Icon size={16} className="text-[color:var(--profecia-gold)]" />
            </div>
            <ul className="space-y-2.5">
                {rows.map((r) => (
                    <li key={r.key} className="text-sm">
                        <div className="flex items-baseline justify-between">
                            <span className="text-[color:var(--text-main)] truncate mr-3">{r.key || "—"}</span>
                            <span className="font-mono text-[color:var(--text-muted)]">{fmt$(r.cost_usd)}</span>
                        </div>
                        <div className="mt-1 h-1.5 bg-[color:var(--surface-hover)] rounded-full overflow-hidden">
                            <div
                                className="h-full bg-[color:var(--profecia-gold)]"
                                style={{ width: `${(r.cost_usd / maxCost) * 100}%` }}
                            />
                        </div>
                        <div className="text-[10px] font-mono text-[color:var(--text-muted)] mt-1">
                            {fmtInt(r.input_tokens)} in · {fmtInt(r.output_tokens)} out · {r.run_count} runs
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}
